import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { createWorker } from 'tesseract.js';

export const ALL_PRODUCT_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.ITF
];

/**
 * Canvas utility helpers to process high-res photos reliably
 */
export function createResizedCanvas(img, maxDim = 1400) {
  const canvas = document.createElement('canvas');
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;

  if (w > maxDim || h > maxDim) {
    if (w > h) {
      h = Math.round((h * maxDim) / w);
      w = maxDim;
    } else {
      w = Math.round((w * maxDim) / h);
      h = maxDim;
    }
  }

  canvas.width = Math.max(w, 100);
  canvas.height = Math.max(h, 100);
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  }
  return { canvas, ctx, width: canvas.width, height: canvas.height };
}

export function createRotatedCanvas(sourceCanvas, degrees = 90) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (degrees === 90 || degrees === 270) {
    canvas.width = sourceCanvas.height;
    canvas.height = sourceCanvas.width;
  } else {
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
  }
  if (ctx) {
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((degrees * Math.PI) / 180);
    ctx.drawImage(sourceCanvas, -sourceCanvas.width / 2, -sourceCanvas.height / 2);
  }
  return { canvas, ctx, width: canvas.width, height: canvas.height };
}

export function applyContrastBoost(ctx, width, height, contrast = 35) {
  try {
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;
    const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
    for (let i = 0; i < data.length; i += 4) {
      data[i] = factor * (data[i] - 128) + 128;
      data[i + 1] = factor * (data[i + 1] - 128) + 128;
      data[i + 2] = factor * (data[i + 2] - 128) + 128;
    }
    ctx.putImageData(imgData, 0, 0);
  } catch (e) {
    console.warn('applyContrastBoost error:', e);
  }
}

export function applyGrayscaleFilter(ctx, width, height) {
  try {
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }
    ctx.putImageData(imgData, 0, 0);
  } catch (e) {
    console.warn('applyGrayscaleFilter error:', e);
  }
}

/**
 * Extracts price and product description from OCR raw text
 */
export function parseLabelOCR(ocrText) {
  if (!ocrText) return { price: null, rawPrice: null, description: '' };

  // Remove secondary unit pricing (e.g. "NESTA EMB 1kg R$ 533,00" or "1L R$ 106,60")
  const primaryText = ocrText
    .split('\n')
    .filter(l => !/nesta\s+emb|emb\s*\d+\s*(?:kg|g|l|ml)|por\s*(?:kg|g|l|ml)/i.test(l))
    .join('\n');

  // Match price pattern: R$ 15,99 / R$15,99 / RS 15.99 / 15,99
  const priceRegex = /(?:R\$|RS|R\s*\$|BB|B\$|R\s*8)?\s*(\d{1,4}[.,]\d{2})/i;
  const match = primaryText.match(priceRegex) || ocrText.match(priceRegex);
  let price = null;
  let rawPrice = null;

  if (match) {
    price = match[1].replace('.', ',');
    rawPrice = parseFloat(match[1].replace(',', '.'));
  }

  // Filter lines for product description
  const cleanLines = ocrText
    .split('\n')
    .map(l => l.trim().replace(/^[^a-zA-Z0-9]+/, ''))
    .filter(l => {
      const lower = l.toLowerCase();
      if (l.length < 3) return false;
      if (lower.includes('nesta emb') || lower.includes('emb 1l') || lower.includes('emb 1kg')) return false;
      if (/^[\d\s.,\-:|=/]+$/.test(l)) return false; // purely numbers/symbols
      if (/^(?:r\$|rs|bb|b\$)\s*\d+/i.test(l)) return false; // purely price line
      return true;
    });

  const nonPriceLines = cleanLines.filter(l => !priceRegex.test(l));
  const description = nonPriceLines.length > 0 ? nonPriceLines[0] : (cleanLines[0] || '');

  return { price, rawPrice, description, rawText: ocrText };
}

export class ProductScanner {
  constructor(containerId, onScanSuccess, onScanError) {
    this.containerId = containerId;
    this.onScanSuccess = onScanSuccess;
    this.onScanError = onScanError;
    this.isScanning = false;
    this.html5QrCode = null;
    this.zoom = 1.8;
    this.isTorchOn = false;
    this.overlayEl = null;
    this.lastDecodedCode = null;
    this.lastDecodedTime = 0;
  }

  async start() {
    if (this.isScanning) return;

    const container = document.getElementById(this.containerId);
    if (!container) throw new Error(`Container #${this.containerId} not found`);

    container.innerHTML = '';
    container.className = 'scanner-video-container';

    // Create child wrapper for Html5Qrcode
    const readerDiv = document.createElement('div');
    readerDiv.id = 'product-scanner-stream-target';
    readerDiv.style.width = '100%';
    readerDiv.style.height = '100%';
    container.appendChild(readerDiv);

    // Overlay visual viewfinder (rectangular for products/shelf labels)
    this.overlayEl = document.createElement('div');
    this.overlayEl.className = 'scan-overlay';
    this.overlayEl.innerHTML = `
      <div class="scan-frame product-mode">
        <div class="corner-bl"></div>
        <div class="corner-br"></div>
        <div class="scan-line"></div>
      </div>
    `;
    container.appendChild(this.overlayEl);

    this.html5QrCode = new Html5Qrcode('product-scanner-stream-target', {
      formatsToSupport: ALL_PRODUCT_FORMATS,
      verbose: false
    });

    const qrConfig = {
      fps: 20,
      aspectRatio: 1.0,
      videoConstraints: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    };

    const handleSuccess = (decodedText, result) => {
      const now = Date.now();
      if (decodedText === this.lastDecodedCode && now - this.lastDecodedTime < 2500) {
        return; // debounce duplicate frames
      }
      this.lastDecodedCode = decodedText;
      this.lastDecodedTime = now;

      if (navigator.vibrate) navigator.vibrate(120);

      const formatName = result?.result?.format?.formatName || 'EAN_13';

      const payload = {
        mode: 'product',
        barcode: decodedText.trim(),
        format: formatName,
        data: decodedText.trim()
      };

      this.stop();
      this.onScanSuccess(decodedText.trim(), payload);
    };

    const handleError = () => {
      // Normal frame-level scanning noise - suppress
    };

    try {
      await this.html5QrCode.start(
        { facingMode: 'environment' },
        qrConfig,
        handleSuccess,
        handleError
      );
      this.isScanning = true;
      this.applyZoom(this.zoom);
    } catch (err) {
      console.warn('1080p camera constraint failed, falling back to basic camera:', err);
      try {
        await this.html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 15 },
          handleSuccess,
          handleError
        );
        this.isScanning = true;
        this.applyZoom(this.zoom);
      } catch (e2) {
        this.isScanning = false;
        if (this.onScanError) this.onScanError(e2);
        throw e2;
      }
    }
  }

  applyZoom(zoomLevel) {
    this.zoom = Math.max(1, Math.min(zoomLevel, 3.5));
    const container = document.getElementById(this.containerId);
    if (container) {
      const videoEl = container.querySelector('video');
      if (videoEl) {
        videoEl.style.transform = `scale(${this.zoom})`;
        videoEl.style.transformOrigin = 'center center';
        videoEl.style.transition = 'transform 180ms ease';
      }
    }
    if (this.html5QrCode && this.html5QrCode.applyVideoConstraints) {
      this.html5QrCode.applyVideoConstraints({
        advanced: [{ zoom: this.zoom }]
      }).catch(() => {});
    }
  }

  setZoom(zoomLevel) {
    this.applyZoom(zoomLevel);
    return true;
  }

  hasTorch() {
    if (!this.html5QrCode) return false;
    try {
      const capabilities = this.html5QrCode.getRunningTrackCapabilities?.();
      return !!capabilities?.torch;
    } catch (e) {
      return false;
    }
  }

  async toggleTorch() {
    if (!this.html5QrCode) return false;
    try {
      this.isTorchOn = !this.isTorchOn;
      await this.html5QrCode.applyVideoConstraints({
        advanced: [{ torch: this.isTorchOn }]
      });
      return this.isTorchOn;
    } catch (err) {
      console.warn('Torch toggle error:', err);
      return false;
    }
  }

  /**
   * Scans an uploaded file (photo) with robust multi-pass downscaling, rotation and OCR
   */
  async scanFile(file, onProgress) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = async () => {
        URL.revokeObjectURL(url);

        try {
          const productResult = await this.scanProductImage(img, file, onProgress);
          resolve(productResult);
        } catch (err) {
          console.warn('scanFile unexpected exception:', err);
          resolve({
            mode: 'product',
            barcode: '',
            format: 'EAN_13',
            price: '',
            rawPrice: null,
            description: '',
            ocrText: '',
            data: ''
          });
        }
      };

      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };

      img.src = url;
    });
  }

  async scanProductImage(img, file, onProgress) {
    let barcodeValue = null;
    let barcodeFormat = 'EAN_13';

    if (onProgress) onProgress('Otimizando resolução da imagem...');

    // 1. Create normalized Canvas (max 1400px) to prevent WASM Out-of-Memory / slowdowns
    const { canvas: normCanvas, ctx: normCtx, width: nw, height: nh } = createResizedCanvas(img, 1400);

    // Multi-angle canvas variants for portrait vs landscape photos (0°, 90°, 270°)
    const angleCanvases = [
      { name: '0°', canvas: normCanvas, w: nw, h: nh },
      { name: '90°', ...createRotatedCanvas(normCanvas, 90) },
      { name: '270°', ...createRotatedCanvas(normCanvas, 270) }
    ];

    if (onProgress) onProgress('Procurando código de barras (1D EAN)...');

    // Pass 1: Native BarcodeDetector across angles
    if ('BarcodeDetector' in window) {
      try {
        const detector = new window.BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code']
        });

        for (const item of angleCanvases) {
          if (barcodeValue) break;
          const barcodes = await detector.detect(item.canvas);
          if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
            barcodeValue = barcodes[0].rawValue.trim();
            barcodeFormat = barcodes[0].format || 'EAN_13';
            break;
          }
        }
      } catch (e) {}

      // Pass 1B: Native BarcodeDetector with contrast boost
      if (!barcodeValue) {
        for (const item of angleCanvases) {
          if (barcodeValue) break;
          try {
            const contrastCanvas = document.createElement('canvas');
            contrastCanvas.width = item.w;
            contrastCanvas.height = item.h;
            const cCtx = contrastCanvas.getContext('2d');
            if (cCtx) {
              cCtx.drawImage(item.canvas, 0, 0);
              applyContrastBoost(cCtx, item.w, item.h, 45);
              const detector = new window.BarcodeDetector({
                formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code']
              });
              const barcodes = await detector.detect(contrastCanvas);
              if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
                barcodeValue = barcodes[0].rawValue.trim();
                barcodeFormat = barcodes[0].format || 'EAN_13';
                break;
              }
            }
          } catch (e) {}
        }
      }
    }

    // 2. Html5Qrcode Multi-Pass Scan (ZXing Engine fallback)
    if (!barcodeValue) {
      if (onProgress) onProgress('Executando varredura profunda de código de barras...');

      const tempId = 'file-scan-temp-' + Math.random().toString(36).substring(2, 9);
      const tempDiv = document.createElement('div');
      tempDiv.id = tempId;
      tempDiv.style.position = 'fixed';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);

      const html5QrCode = new Html5Qrcode(tempId, {
        formatsToSupport: ALL_PRODUCT_FORMATS,
        verbose: false
      });

      try {
        // 2A: Scan normalized canvas (0° and 90°)
        for (const item of angleCanvases.slice(0, 2)) {
          if (barcodeValue) break;
          const normBlob = await new Promise(r => item.canvas.toBlob(r, 'image/png'));
          if (normBlob) {
            const normFile = new File([normBlob], 'norm.png', { type: 'image/png' });
            try {
              const fullRes = await html5QrCode.scanFileV2(normFile, true);
              if (fullRes && fullRes.decodedText) {
                barcodeValue = fullRes.decodedText.trim();
                barcodeFormat = fullRes.result?.format?.formatName || 'EAN_13';
                break;
              }
            } catch (e) {}
          }
        }

        // 2B: Multi-scale region crops (Bottom half, Right half, Center strip)
        if (!barcodeValue) {
          const cropConfigs = [
            // Bottom 50% (shelf tag layout)
            { sx: 0.05, sy: 0.40, sw: 0.90, sh: 0.55, scale: 1.5 },
            // Right 55%
            { sx: 0.45, sy: 0.10, sw: 0.52, sh: 0.85, scale: 1.8 },
            // Center horizontal strip
            { sx: 0.05, sy: 0.20, sw: 0.90, sh: 0.60, scale: 1.5 }
          ];

          for (const crop of cropConfigs) {
            if (barcodeValue) break;

            const cCanvas = document.createElement('canvas');
            const cCtx = cCanvas.getContext('2d');

            const sx = Math.floor(nw * crop.sx);
            const sy = Math.floor(nh * crop.sy);
            const sw = Math.floor(nw * crop.sw);
            const sh = Math.floor(nh * crop.sh);

            const pad = 20;
            const targetW = Math.floor(sw * crop.scale) + pad * 2;
            const targetH = Math.floor(sh * crop.scale) + pad * 2;

            cCanvas.width = targetW;
            cCanvas.height = targetH;

            if (cCtx) {
              cCtx.fillStyle = '#FFFFFF';
              cCtx.fillRect(0, 0, targetW, targetH);
              cCtx.drawImage(normCanvas, sx, sy, sw, sh, pad, pad, Math.floor(sw * crop.scale), Math.floor(sh * crop.scale));
              applyContrastBoost(cCtx, targetW, targetH, 35);

              const cropBlob = await new Promise(r => cCanvas.toBlob(r, 'image/png'));
              if (cropBlob) {
                const cropFile = new File([cropBlob], 'crop.png', { type: 'image/png' });
                try {
                  const res = await html5QrCode.scanFileV2(cropFile, true);
                  if (res && res.decodedText) {
                    barcodeValue = res.decodedText.trim();
                    barcodeFormat = res.result?.format?.formatName || 'EAN_13';
                    break;
                  }
                } catch (e) {}
              }
            }
          }
        }
      } finally {
        try { await html5QrCode.clear(); } catch(e) {}
        tempDiv.remove();
      }
    }

    // 3. Local Tesseract OCR on Normalized Canvas (max 1000px for speed)
    let ocrResult = { price: null, rawPrice: null, description: '', rawText: '' };
    try {
      if (onProgress) onProgress('Lendo texto e preço da etiqueta via OCR...');

      const { canvas: ocrCanvas, ctx: ocrCtx, width: ow, height: oh } = createResizedCanvas(normCanvas, 1000);
      if (ocrCtx) {
        applyContrastBoost(ocrCtx, ow, oh, 30);
      }

      const worker = await createWorker('por+eng', 1, {
        logger: m => {
          if (m.status === 'recognizing text' && onProgress) {
            const pct = Math.round((m.progress || 0) * 100);
            onProgress(`Reconhecendo etiqueta (${pct}%)...`);
          }
        }
      });

      const res = await worker.recognize(ocrCanvas);
      await worker.terminate();

      if (res && res.data && res.data.text) {
        ocrResult = parseLabelOCR(res.data.text);
      }
    } catch (ocrErr) {
      console.warn('OCR processing error:', ocrErr);
    }

    return {
      mode: 'product',
      barcode: barcodeValue || '',
      format: barcodeFormat,
      price: ocrResult.price || '',
      rawPrice: ocrResult.rawPrice || null,
      description: ocrResult.description || '',
      ocrText: ocrResult.rawText || '',
      data: barcodeValue || ocrResult.description || 'PRODUTO'
    };
  }

  async stop() {
    this.isScanning = false;
    if (this.html5QrCode) {
      try {
        if (this.html5QrCode.isScanning) {
          await this.html5QrCode.stop();
        }
        this.html5QrCode.clear();
      } catch (e) {
        console.warn('Html5Qrcode stop error:', e);
      }
      this.html5QrCode = null;
    }
  }
}


